import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, ChatIconOutline, ChatMessageDisplay, CheckMarkIcon, CloseIcon, LeftCaretIcon, ListHeader, RowsContainer, ScrollableContainer, SpeakerItem, UpArrowIcon, } from '@streamr/tv-ui';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import { UserService } from '../services/user/UserService';
import { AcceptedParticipantType, UserRole, } from '../services/user/UserServiceOutput';
import { TextStyle } from '../utils/styled';
/**
 * Panel component that displays speakers, speaker requests, and allows private chats.
 * This is designed to be rendered in the sidebar.
 */
export function SpeakersPanel({ onClose }) {
    const [activePerson, setActivePerson] = useState(null);
    return (_jsx(SpeakersPanelRoot, { children: _jsx(ScrollableContainer, { children: _jsx(Content, { children: !activePerson ? (_jsx(PeopleView, { onClose: onClose, onChatButtonClick: (person) => {
                        setActivePerson(person);
                    } })) : (_jsx(ChatView, { person: activePerson, onBackButtonClick: () => {
                        setActivePerson(null);
                    }, onCloseButtonClick: onClose })) }) }) }));
}
function PeopleView({ onClose, onChatButtonClick }) {
    const incomingRequests = UserService.getOutput().useIncomingRequests();
    const acceptedParticipantType = UserService.getOutput().useAcceptedParticipantType();
    const isCallMode = acceptedParticipantType === AcceptedParticipantType.SPEAKERS;
    const speakerRequests = incomingRequests.filter((request) => request.userRole === UserRole.SPEAKER);
    const guestRequests = incomingRequests.filter((request) => request.userRole === UserRole.GUEST);
    const speakers = UserService.getOutput().useSpeakers();
    const guests = UserService.getOutput().useGuests();
    const displayNames = UserService.getOutput().useDisplayNames();
    const permissionService = UserService.getNotificationInterface();
    const resolveName = (userId) => displayNames[userId] || userId;
    const participantActions = (userId, status) => (_jsxs(_Fragment, { children: [_jsx(SpeakerActionButton, { kind: "pill", onClick: () => {
                    permissionService.notifyRevokePermission(userId, status);
                }, children: _jsx(CloseIcon, {}) }), !isCallMode && (_jsx(SpeakerActionButton, { kind: "pill", onClick: () => {
                    onChatButtonClick?.({
                        id: userId,
                        name: resolveName(userId),
                    });
                }, children: _jsx(ChatIconOutline, {}) }))] }));
    const requestActions = (request) => (_jsxs(_Fragment, { children: [_jsx(SpeakerActionGlossyButton, { kind: "pill", className: "tv-ui-shiny-border", onClick: () => {
                    permissionService.notifyAcceptPermission(request.userId, request.userRole);
                }, children: _jsx(CheckMarkIcon, {}) }), _jsx(SpeakerActionButton, { kind: "pill", onClick: () => {
                    permissionService.notifyRejectPermission(request.userId, request.userRole);
                }, children: _jsx(CloseIcon, {}) }), !isCallMode && (_jsx(SpeakerActionButton, { kind: "pill", onClick: () => {
                    onChatButtonClick?.({
                        id: request.userId,
                        name: resolveName(request.userId),
                    });
                }, children: _jsx(ChatIconOutline, {}) }))] }));
    return (_jsxs(_Fragment, { children: [_jsxs(Header, { children: [_jsx(Title, { children: "People" }), onClose && (_jsx(HeaderButton, { kind: "pill", onClick: onClose, children: _jsx(CloseIcon, {}) }))] }), _jsxs(Lists, { children: [guestRequests.length > 0 && (_jsxs(ListWrap, { children: [_jsx(ListHeader, { label: "Guest requests", count: guestRequests.length }), _jsx(ListItems, { children: guestRequests.map((request) => (_jsx(SpeakerItem, { name: resolveName(request.userId), actions: requestActions(request) }, request.userId))) })] })), speakerRequests.length > 0 && (_jsxs(ListWrap, { children: [_jsx(ListHeader, { label: "Speaker requests", count: speakerRequests.length }), _jsx(ListItems, { children: speakerRequests.map((request) => (_jsx(SpeakerItem, { name: resolveName(request.userId), actions: requestActions(request) }, request.userId))) })] })), guests.length > 0 && (_jsxs(ListWrap, { children: [_jsx(ListHeader, { label: "Guests", count: guests.length }), _jsx(ListItems, { children: guests.map((guestId) => (_jsx(SpeakerItem, { name: resolveName(guestId), actions: participantActions(guestId, UserRole.GUEST) }, guestId))) })] })), speakers.length > 0 && (_jsxs(ListWrap, { children: [_jsx(ListHeader, { label: "Speakers", count: speakers.length }), _jsx(ListItems, { children: speakers.map((userId) => (_jsx(SpeakerItem, { name: resolveName(userId), actions: participantActions(userId, UserRole.SPEAKER) }, userId))) })] }))] })] }));
}
function ChatView({ person, onBackButtonClick, onCloseButtonClick, }) {
    const privateChatMessages = UserService.getOutput().usePrivateChatMessagesFromUser(person.id);
    const permissionService = UserService.getNotificationInterface();
    const inputRef = useRef(null);
    const sendMessage = () => {
        if (inputRef.current) {
            const message = inputRef.current.value.trim();
            if (message) {
                permissionService.notifySendPrivateMessage(person.id, message);
                inputRef.current.value = '';
            }
        }
    };
    return (_jsxs(_Fragment, { children: [_jsxs(Header, { children: [onBackButtonClick && (_jsx(HeaderButton, { kind: "pill", onClick: onBackButtonClick, children: _jsx(LeftCaretIcon, {}) })), _jsx(Title, { children: person.name }), onCloseButtonClick && (_jsx(HeaderButton, { kind: "pill", onClick: onCloseButtonClick, children: _jsx(CloseIcon, {}) }))] }), _jsxs(ChatContainer, { children: [_jsx(ChatMessagesContainer, { children: privateChatMessages.map((message) => (_jsx(ChatMessageDisplay, { authorNickname: message.isSent ? 'You' : person.name, content: message.message }, message.timestamp))) }), _jsxs(ChatInputContainer, { children: [_jsx(ChatInput, { ref: inputRef, placeholder: "Type a message...", onKeyDown: (e) => {
                                    if (e.key === 'Enter') {
                                        sendMessage();
                                    }
                                } }), _jsx(SendButton, { kind: "pill", onClick: sendMessage, children: _jsx(UpArrowIcon, {}) })] })] })] }));
}
const SpeakersPanelRoot = styled.div `
    height: 100%;
    width: 100%;
    background: transparent;
    position: relative;
`;
const Content = styled(RowsContainer) `
    width: 100%;
    gap: 1rem;
    padding: 1.5rem;
    box-sizing: border-box;
`;
const Header = styled.div `
    ${TextStyle.Heading_3};
    display: flex;
    align-items: center;
    gap: 0.75rem;
    position: relative;
    z-index: 1;
`;
const Title = styled.div `
    font-weight: 600;
    flex-grow: 1;
    color: white;
`;
const HeaderButton = styled(Button) `
    --Button_Height: 1.75rem;
    --Button_Width: 1.75rem;

    svg {
        transform: scale(0.75);
    }
`;
const Lists = styled.div `
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    width: 100%;
`;
const ListWrap = styled.div ``;
const ListItems = styled.div `
    & > div {
        margin-top: 0.5rem;
    }
`;
const SpeakerActionGlossyButton = styled(Button) `
    --Button_Background: rgba(47, 47, 47, 0.6);
    --Button_Width: 2rem;
    --Button_Height: 2rem;
    --tv-ui-shiny-border-thickness: 0.0625rem;

    svg {
        transform: scale(0.85);
    }
`;
const SpeakerActionButton = styled(Button) `
    --Button_Background: rgba(255, 255, 255, 0);
    --Button_HoverBackground: rgba(255, 255, 255, 0.05);
    --Button_Width: 2rem;
    --Button_Height: 2rem;

    svg {
        transform: scale(0.85);
    }
`;
const ChatContainer = styled.div `
    display: flex;
    flex-direction: column;
    gap: 1rem;
    flex: 1;
    min-height: 0;
`;
const ChatMessagesContainer = styled.div `
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex: 1;
    overflow-y: auto;
`;
const ChatInputContainer = styled.div `
    display: flex;
    gap: 0.5rem;
    align-items: center;
`;
const ChatInput = styled.input `
    flex: 1;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 0.5rem;
    padding: 0.75rem;
    color: white;
    font-size: 0.875rem;

    &::placeholder {
        color: rgba(255, 255, 255, 0.5);
    }

    &:focus {
        outline: none;
        border-color: rgba(255, 255, 255, 0.2);
    }
`;
const SendButton = styled(Button) `
    --Button_Width: 2.5rem;
    --Button_Height: 2.5rem;
    --Button_Background: rgba(255, 64, 21, 0.8);
    --Button_HoverBackground: #ff4015;
`;
